import { motion } from "framer-motion";
import { Flame, Clock } from "lucide-react";
import { SectionHeader } from "./SectionHeader";
import { CTAButton } from "./CTAButton";
import useCountdown from "../../hooks/useCountdown";

const reasons = [
  "Promo hanya bulan ini",
  "Harga kembali normal kapan saja",
  "Slot aktivasi terbatas",
  "Aktivasi otomatis setelah pembayaran",
  "Pengiriman via Email & WhatsApp",
  "Proses sangat cepat",
  "Tanpa menunggu admin online",
];

const TimeBox = ({ value, label }) => (
  <div className="flex flex-col items-center">
    <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-white/10 backdrop-blur border border-white/15 flex items-center justify-center">
      <span className="font-heading text-3xl md:text-4xl font-extrabold text-white tabular-nums">{value}</span>
    </div>
    <span className="mt-2 text-[11px] font-bold uppercase tracking-[0.18em] text-blue-200">{label}</span>
  </div>
);

export const Urgency = () => {
  const { h, m, s } = useCountdown();

  return (
    <section className="py-20 md:py-28 bg-[#0B1530] relative overflow-hidden" id="urgensi">
      <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-blue-600/20 blur-[140px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-cyan-500/15 blur-[120px] pointer-events-none" />
      <div className="relative max-w-6xl mx-auto px-5 md:px-8">
        <SectionHeader
          title="Kenapa harus order sekarang?"
          sub="Harga promo ini tidak akan bertahan lama. Setiap hari yang Anda tunda adalah risiko kehilangan penawaran ini."
          light
        />

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7 }}
            className="rounded-3xl bg-white/[0.06] backdrop-blur-xl border border-white/10 p-8 md:p-10"
            data-testid="countdown-card"
          >
            <div className="flex items-center gap-2 mb-6">
              <Clock className="w-5 h-5 text-red-400" strokeWidth={2.5} />
              <p className="text-sm font-bold uppercase tracking-[0.16em] text-red-400">Promo berakhir dalam</p>
            </div>
            <div className="flex items-center gap-3 md:gap-4">
              <TimeBox value={h} label="Jam" />
              <span className="text-white/40 font-heading text-3xl font-bold pb-6">:</span>
              <TimeBox value={m} label="Menit" />
              <span className="text-white/40 font-heading text-3xl font-bold pb-6">:</span>
              <TimeBox value={s} label="Detik" />
            </div>
            <p className="mt-6 text-blue-200/80 text-sm leading-relaxed">
              Harga <span className="font-bold text-white">Rp79.000</span> hanya berlaku untuk slot aktivasi hari ini. Besok bisa jadi
              sudah kembali ke <span className="line-through">Rp5.562.000</span>.
            </p>
            <div className="mt-7">
              <CTAButton source="urgency-countdown" size="md" testId="urgency-order-button" className="w-full sm:w-auto" />
            </div>
          </motion.div>

          <div className="relative pl-8">
            <div className="absolute left-[11px] top-2 bottom-2 w-px bg-gradient-to-b from-blue-500 via-cyan-400/60 to-transparent" />
            {reasons.map((reason, i) => (
              <motion.div
                key={reason}
                initial={{ opacity: 0, x: 24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className="relative pb-7 last:pb-0"
                data-testid={`urgency-reason-${i}`}
              >
                <span className="absolute -left-8 top-0.5 w-6 h-6 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center shadow-[0_0_16px_rgba(249,115,22,0.5)]">
                  <Flame className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />
                </span>
                <p className="font-semibold text-white text-base md:text-lg leading-snug">{reason}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
